import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import NoPhotographyIcon from "@mui/icons-material/NoPhotography";
import { userSlice } from "../redux/user/userSlice";
import {
  getAccessLabel,
  getProjectStatusDisplay,
  getProjectStatusFromSettings,
} from "../utils/projectAccess";
import { getProjectAnalyticsPreview } from "../utils/analyticsDemoData";
import {
  getProjectCardAnalyticsRows,
  getProjectCardMetaRows,
  getProjectCardKind,
  getProjectCardTone,
} from "../utils/projectCardMeta";
import { getContentTypeLabel, CONTENT_TYPES, resolveGameContentType } from "../utils/contentType";
import { getServerMediaUrl } from "../utils/mediaUrl";
import ProjectCardShareActions from "./ProjectCardShareActions";
import useMediaQuery from "../hooks/useMediaQuery";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const { selectProject, showDialog } = userSlice;

function ProjectCardAnalytics({ rows, defaultCollapsed = false }) {
  const [open, setOpen] = useState(!defaultCollapsed);

  useEffect(() => {
    setOpen(!defaultCollapsed);
  }, [defaultCollapsed]);

  return (
    <div
      className={`app-project-card__analytics${
        open ? "" : " app-project-card__analytics--collapsed"
      }`}
      onClick={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        className="app-project-card__analytics-head"
        aria-expanded={open}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setOpen((value) => !value);
        }}
      >
        <span>Аналитика</span>
        {open ? (
          <ExpandLessIcon fontSize="small" aria-hidden />
        ) : (
          <ExpandMoreIcon fontSize="small" aria-hidden />
        )}
      </button>
      {open ? (
        <ul className="app-project-card__analytics-list">
          {rows.map((row) => (
            <li
              key={row.label}
              className={`app-project-card__analytics-row${
                row.accent ? " app-project-card__analytics-row--accent" : ""
              }`}
            >
              <span className="app-project-card__analytics-label">{row.label}</span>
              <span className="app-project-card__analytics-value">{row.value}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function renderProjectPoster(poster, title) {
  const src = getServerMediaUrl(poster);
  if (!src) return null;
  return <img src={src} alt={title} />;
}

export default function Author() {
  const dispatch = useDispatch();
  const [gamesData, setGamesData] = useState([]);
  const data = useSelector((state) => state.user?.games);
  const isMobile = useMediaQuery("(max-width: 900px)");

  useEffect(() => {
    if (data) {
      setGamesData(data);
    }
  }, [data]);

  const visibleGames = gamesData.filter(
    (game) => resolveGameContentType(game) !== CONTENT_TYPES.knowledge_base,
  );

  return (
    <div>
      {!visibleGames.length ? (
        <div className="app-empty">
          <h2 className="app-empty__title">Пока нет проектов</h2>
          <p className="app-empty__desc">
            Создайте сценарий или тренажёр — кнопка также доступна в меню слева.
            База знаний открывается отдельно из меню.
          </p>
          <Button
            variant="contained"
            className="app-btn app-btn--primary app-empty__cta"
            startIcon={<AddIcon />}
            onClick={() => dispatch(showDialog("new"))}
          >
            Создать
          </Button>
        </div>
      ) : (
        <div className="app-project-grid">
          {visibleGames.map((el) => {
            const status = getProjectStatusDisplay(el.settings);
            const accessLabel = getAccessLabel(el.settings);
            const analyticsPreview = getProjectAnalyticsPreview(el);
            const kind = getProjectCardKind(el);
            const tone = getProjectCardTone(kind);
            const analyticsRows = getProjectCardAnalyticsRows(el, analyticsPreview);
            const metaRows = getProjectCardMetaRows(el);
            const isPublished = getProjectStatusFromSettings(el.settings) === "published";

            return (
              <Link
                key={el._id}
                to={`/main/${el._id}`}
                state={{ projectId: el._id, type: "edit" }}
                className={`app-project-card app-project-card--link app-project-card--${tone}`}
                onClick={() => dispatch(selectProject(el._id))}
              >
                <div className="app-project-card__head">
                  <span className={`app-project-card__type app-project-card__type--${tone}`}>
                    {getContentTypeLabel(kind)}
                  </span>
                  <div className="app-project-card__title">{el.title}</div>
                </div>

                <div className="app-project-card__media">
                  {el.poster ? (
                    renderProjectPoster(el.poster, el.title)
                  ) : (
                    <div className="upload-container">
                      <div className="upload-icon">
                        <NoPhotographyIcon />
                      </div>
                    </div>
                  )}
                </div>

                <ProjectCardShareActions gameId={el._id} isPublished={isPublished} />

                <ProjectCardAnalytics rows={analyticsRows} defaultCollapsed={isMobile} />

                <div className="app-project-card__meta">
                  <div className="app-project-card__meta-row">
                    <span>Статус</span>
                    <span
                      className={`app-project-card__status app-project-card__status--${status.tone}`}
                    >
                      {status.label}
                    </span>
                  </div>
                  <div className="app-project-card__meta-row">
                    <span>Доступ</span>
                    <span>{accessLabel}</span>
                  </div>
                  {metaRows.map((row) => (
                    <div key={row.label} className="app-project-card__meta-row">
                      <span>{row.label}</span>
                      <span className="app-project-card__meta-value">{row.value}</span>
                    </div>
                  ))}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
